import React, { useEffect, useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Box,
  useTheme,
  useMediaQuery,
  Grow,
  Fade,
  Slide,
  Container,
  Chip,
  Divider,
  CircularProgress,
  Button,
} from '@mui/material'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Logout,
  Settings,
  Bookmark,
  Chat,
  ExpandMore,
  ExpandLess,
  Person,
} from '@mui/icons-material'
import { useAppDispatch, useAppSelector } from '@store/index'
import { logout, login } from '@store/slices/authSlice'
import { toggleSidebar } from '@store/slices/appSlice'
import { addNotification } from '@store/slices/notificationSlice'
import { useNavigate, useLocation } from 'react-router-dom'
import { MockAuthService } from '../../services/mockAuthService'
import SearchBarForPatient from '../patient/pateintSearch'
import { User, UserRole } from '../../types/index'
import LOGO from '../../assets/pillarThera.png'
import { useAuth } from '@/hooks/useAuth'
import api from '@services/api'
import {
  getFamilyMembers,
  loginAsFamilyMember,
  FamilyMember,
} from '@/services/familyService'

export const Navbar: React.FC = () => {
  const theme = useTheme()
  const { isAuthenticated, role } = useAuth()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const relationship = localStorage.getItem('relationship')
  const { user } = useAppSelector(state => state.auth)
  const { notifications } = useAppSelector(state => state.app)
  const [familyMembers, setFamilyMembers] = React.useState<FamilyMember[]>([])
  const [familyLoading, setFamilyLoading] = React.useState(false)
  const [switchingMemberId, setSwitchingMemberId] = React.useState<
    string | null
  >(null)
  const [familyError, setFamilyError] = React.useState<string | null>(null)

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const [notificationAnchor, setNotificationAnchor] =
    React.useState<null | HTMLElement>(null)

  // Search state
  const [searchLocation, setSearchLocation] = React.useState<string>('')
  const [unopenedCount, setUnopenedCount] = useState(0)
  const [searchRadius, setSearchRadius] = React.useState<string>('')
  const [searchInsurance, setSearchInsurance] = React.useState<string | null>(
    null
  )
  const [searchSpecialization, setSearchSpecialization] = React.useState<
    string | null
  >(null)

  // Handle location change - clear radius when location is cleared
  const handleLocationChange = (value: string | null) => {
    setSearchLocation(value || '')
    // Clear radius when location is cleared
    if (!value || !value.trim()) {
      setSearchRadius('')
    }
  }

  const [notification, setNotification] = React.useState<any[]>([]) // You can specify a more specific type if you know the structure
  const [notificationLoading, setNotificationLoading] =
    React.useState<boolean>(false)
  const [showAllNotifications, setShowAllNotifications] =
    React.useState<boolean>(false)
  const NOTIFICATIONS_TO_SHOW = 10

  const unreadNotifications = notifications.filter(n => !n.read).length
  const isPrimaryClient =
    user?.role === UserRole.CLIENT &&
    (!relationship || relationship.trim().length === 0)

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleNotificationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget)
  }

  const handleNotificationMenuClose = () => {
    setNotificationAnchor(null)
    setShowAllNotifications(false) // Reset to show only top 10 when menu closes
  }

  const handleLogout = async () => {
    try {
      if (user.role === 'client') {
        navigate('/patient-login')
        localStorage.clear()
      } else if (user.role === 'certificate_manager') {
        navigate('/certificate-login')
        localStorage.clear()
      } else {
        navigate('/admin-login')
        localStorage.clear()
      }
      await MockAuthService.logout()
      localStorage.clear()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      if (user.role === 'client') {
        navigate('/patient-login')
        localStorage.clear()
      } else if (user.role === 'certificate_manager') {
        navigate('/certificate-login')
        localStorage.clear()
      } else {
        navigate('/admin-login')
        localStorage.clear()
      }
      dispatch(logout())
      handleProfileMenuClose()
      handleNotificationMenuClose()
      localStorage.clear()
    }
  }

  const fetchNotifications = async () => {
    setNotificationLoading(true)
    try {
      const endpoint =
        user.role === UserRole.ADMIN
          ? '/web-notifications'
          : 'web-notifications/patient'
      const response = await api.get(endpoint)
      const data = response?.data?.notifications ?? []
      const normalizedData = data.map((n: any) => ({
        ...n,
        read: n.read !== undefined ? n.read : n.isRead !== undefined ? n.isRead : false,
      }))
      setNotification(normalizedData)
    } catch (error) {
      console.error('Error fetching notifications:', error)
      setNotification([])
    } finally {
      setNotificationLoading(false)
    }
  }

  const fetchUnopenedCount = async () => {
    try {
      const endpoint =
        user.role === UserRole.ADMIN
          ? '/web-notifications/unopened-count'
          : '/web-notifications/unopened-count-patient'
      const response = await api.get(endpoint)
      setUnopenedCount(response?.data?.unOpenedCount ?? 0)
    } catch (error) {
      console.error('Error fetching unopened count:', error)
      setUnopenedCount(0)
    }
  }

  const markNotificationAsRead = async (notificationId: string) => {
    try {
      const endpoint =
        user.role === UserRole.ADMIN
          ? `/web-notifications/read/${notificationId}`
          : `/web-notifications/read-patient/${notificationId}`
      const response = await api.patch(endpoint)
      if (response?.status === 200) {
        fetchNotifications()
        fetchUnopenedCount()
      }
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  // Function to handle notification click and navigate based on notificationType
  const handleNotificationClick = (notification: any) => {
    const { notificationType, patientId, therapistId, _id } = notification

    markNotificationAsRead(_id)
    handleNotificationMenuClose()

    if (user.role === UserRole.ADMIN) {
      switch (notificationType) {
        case 'new_provider':
          navigate('/Provider')
          break
        case 'new_patient':
          navigate('/Patient')
          break
        case 'contract':
        case 'contract_pending':
          navigate('/booking-management')
          break
        default:
          break
      }
      return
    }

    // Patient navigation
    switch (notificationType) {
      case 'chat':
        if (patientId?._id) {
          navigate(`/messages?patientId=${patientId._id}`)
        } else {
          navigate(`/messages?patientId=${user?.id}`)
        }
        break
      case 'proposal_received':
        navigate('/patient-proposals')
        break
      case 'contract_pending':
        navigate('/contracts')
        break
      default:
        console.log('Unhandled notification type:', notificationType)
        break
    }
  }

  useEffect(() => {
    if (user.role === 'client' || user.role === UserRole.ADMIN) {
      fetchNotifications()
      fetchUnopenedCount()

      const countInterval = setInterval(() => {
        fetchUnopenedCount()
      }, 30000)

      return () => {
        clearInterval(countInterval)
      }
    }
  }, [])

  // Also refresh notifications and count when notification menu is opened
  useEffect(() => {
    if (notificationAnchor) {
      fetchNotifications()
      fetchUnopenedCount()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationAnchor])

  // Format date and time from createdAt field
  const formatNotificationDate = (createdAt: string) => {
    if (!createdAt) return ''

    const date = new Date(createdAt)
    const now = new Date()

    // Get local date components for proper date comparison
    const dateYear = date.getFullYear()
    const dateMonth = date.getMonth()
    const dateDay = date.getDate()
    const nowYear = now.getFullYear()
    const nowMonth = now.getMonth()
    const nowDay = now.getDate()

    // Calculate difference in calendar days (not just milliseconds)
    const dateStartOfDay = new Date(dateYear, dateMonth, dateDay).getTime()
    const nowStartOfDay = new Date(nowYear, nowMonth, nowDay).getTime()
    const diffInMs = now.getTime() - date.getTime()
    const diffInMinutes = Math.floor(diffInMs / 60000)
    const diffInCalendarDays = Math.floor(
      (nowStartOfDay - dateStartOfDay) / 86400000
    )

    // Format for same day: "Today at 2:30 PM"
    if (diffInCalendarDays === 0) {
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      if (diffInMinutes < 1) {
        return 'Just now'
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`
      } else {
        return `Today at ${timeStr}`
      }
    }

    // Format for yesterday: "Yesterday at 2:30 PM"
    if (diffInCalendarDays === 1) {
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      return `Yesterday at ${timeStr}`
    }

    // Format for this week: "Monday at 2:30 PM"
    if (diffInCalendarDays < 7) {
      const dayName = date.toLocaleDateString('en-US', { weekday: 'long' })
      const timeStr = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      return `${dayName} at ${timeStr}`
    }

    // Format for older dates: "Jan 15, 2026 at 2:30 PM"
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: dateYear !== nowYear ? 'numeric' : undefined,
    })
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    return `${dateStr} at ${timeStr}`
  }

  const handleProfile = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    handleProfileMenuClose()
    navigate('/account-settings', {
      state: { from: location.pathname === '/dashboard' ? 'dashboard' : undefined }
    })
  }

  const handleSearch = (searchParams?: {
    insurance?: string | null
    specialty?: string | null
    insuranceId?: string | null
    specialtyId?: string | null
    radius?: string | null
    location?: string | null
    lat?: number | string | null
    lng?: number | string | null
  }) => {
    const targetPath =
      user.role === 'client' ? '/find-therapists' : '/find-therapist'
    const isOnFindTherapistPage = location.pathname === targetPath

    // Prepare search params - always pass an object even if all values are null
    // This ensures API is called even when no filters are selected
    const finalSearchParams = searchParams || {
      insurance: searchInsurance || null,
      specialty: searchSpecialization || null,
      location: searchLocation || null,
      radius: searchRadius || null,
      insuranceId: null,
      specialtyId: null,
      lat: null,
      lng: null,
    }

    // If already on FindTherapist page, use replace to update state and trigger search
    // This works like the "Search Again" button - directly triggers the API
    if (isOnFindTherapistPage) {
      navigate(targetPath, {
        replace: true,
        state: {
          searchParams: finalSearchParams,
          hasSearched: true,
        },
      })
    } else {
      // If not on FindTherapist page, navigate to it with search params
      navigate(targetPath, {
        state: {
          searchParams: finalSearchParams,
          hasSearched: true,
        },
      })
    }
  }

  const handleSwitchToFamilyMember = async (member: FamilyMember) => {
    try {
      setSwitchingMemberId(member._id)
      const response = await loginAsFamilyMember(member._id)
      const authUser = {
        id:
          response.familyMember?.id || response.familyMember?._id || member._id,
        firstName: response.familyMember?.firstName || member.firstName,
        lastName: response.familyMember?.lastName || member.lastName,
        email: response.familyMember?.email || member.email,
        role: UserRole.CLIENT,
        originalRole: UserRole.CLIENT,
        avatar: response.familyMember?.avatar || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        relationship: response.familyMember?.relationship,
      }

      localStorage.setItem(
        'profilePhotoUrl',
        response.familyMember?.profilePhotoUrl || member.profilePhotoUrl || ''
      )
      localStorage.setItem(
        'relationship',
        response.familyMember?.relationship || member.relationship || ''
      )

      const currentAuth = localStorage.getItem('auth')
      if (currentAuth) {
        try {
          const parsedAuth = JSON.parse(currentAuth)
          localStorage.setItem(
            'originalPatient',
            JSON.stringify(parsedAuth.user)
          )
          localStorage.setItem('originalAuth', currentAuth)
        } catch (error) {
          console.warn('Failed to parse current auth payload', error)
        }
      }

      dispatch(
        login({
          user: authUser,
          token: response.access_token,
          refreshToken: response.refresh_token,
        })
      )

      dispatch(
        addNotification({
          type: 'success',
          title: 'Switched Account',
          message: `You are now managing ${authUser.firstName} ${authUser.lastName}'s account.`,
        })
      )

      navigate('/dashboard', { replace: true })
      handleProfileMenuClose()
    } catch (error: any) {
      console.error('Login as family member failed:', error)
      const errorMessage =
        error?.response?.data?.message ||
        (error?.response?.status === 401
          ? 'Unauthorized access.'
          : error?.response?.status === 404
            ? 'Family member not found.'
            : 'Failed to switch accounts. Please try again.')

      dispatch(
        addNotification({
          type: 'error',
          title: 'Switch Failed',
          message: errorMessage,
        })
      )
      toast.error(errorMessage)
    } finally {
      setSwitchingMemberId(null)
    }
  }

  React.useEffect(() => {
    handleProfileMenuClose()
    handleNotificationMenuClose()
  }, [location.pathname])

  React.useEffect(() => {
    let isMounted = true

    const fetchMembers = async () => {
      if (!isPrimaryClient) {
        setFamilyMembers([])
        return
      }

      setFamilyLoading(true)
      setFamilyError(null)

      try {
        const response = await getFamilyMembers()
        const members = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : []
        if (isMounted) {
          setFamilyMembers(members)
        }
      } catch (error) {
        console.error('Failed to fetch family members:', error)
        if (isMounted) {
          setFamilyError('Unable to load family members right now.')
        }
      } finally {
        if (isMounted) {
          setFamilyLoading(false)
        }
      }
    }

    fetchMembers()

    return () => {
      isMounted = false
    }
  }, [isPrimaryClient])

  // Profile picture: prefer user from auth (includes profilePhotoUrl from login), then localStorage (e.g. family member switch)
  const profilePhotoUrl =
    user?.profilePhotoUrl ||
    localStorage.getItem('profilePhotoUrl') ||
    user?.avatar
  return (
    <AppBar
      position="fixed"
      sx={{
        bgcolor: 'white',
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: theme.zIndex.drawer + 1,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Toolbar
        sx={{
          px: { xs: 1, sm: 2, md: 3 },
          minHeight: { xs: '64px', sm: '70px' },
        }}
      >
        {/* Left Section - Logo & Mobile Menu */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: { xs: 1, md: 0.2 },
            minWidth: { xs: 'auto', md: 180 },
            mr: { xs: 1, sm: 2 },
          }}
        >
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={() => dispatch(toggleSidebar())}
              sx={{
                mr: { xs: 1, sm: 2 },
                color: 'text.primary',
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Slide direction="right" in timeout={800}>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: 'primary.main',
                fontSize: {
                  xs: '1.1rem',
                  sm: '1.3rem',
                  md: '1.5rem',
                  lg: '1.75rem',
                },
                whiteSpace: 'nowrap',
              }}
            >
              <img
                src={LOGO}
                alt="LOGO"
                style={{
                  width: 'auto',
                  height: '3.5rem',
                }}
              />
            </Typography>
          </Slide>
        </Box>

        {/* Center Section - Search Bar */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: { xs: 2, sm: 1.5, md: 1 },
            minWidth: 0, // Important for flex shrinking
            mx: { xs: 0.5, sm: 1, md: 2 },
            maxWidth: { lg: 800 },
          }}
        >
          {user.role === 'client' && (
            <Fade in timeout={800} style={{ transitionDelay: '200ms' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  maxWidth: '100%',
                  gap: { xs: 0.5, sm: 1, md: 2 },
                }}
              >
                {/* Search Labels - Hidden on smallest screens */}
                {!isSmallMobile && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      minWidth: 'fit-content',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'primary.main',
                        fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1.1rem' },
                        fontWeight: 800,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Search
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'primary.main',
                        fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1.1rem' },
                        fontWeight: 800,
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Providers
                    </Typography>
                  </Box>
                )}

                {/* Search Bar */}
                <Grow in timeout={1000} style={{ transitionDelay: '400ms' }}>
                  <Box
                    sx={{
                      width: '100%',
                      flex: 1,
                      minWidth: 0, // Important for responsive behavior
                    }}
                  >
                    <SearchBarForPatient
                      locations={searchLocation}
                      radius={searchRadius}
                      insurance={searchInsurance}
                      needs={searchSpecialization}
                      onLocationChange={handleLocationChange}
                      onRadiusChange={setSearchRadius}
                      onInsuranceChange={setSearchInsurance}
                      onNeedsChange={setSearchSpecialization}
                      onSearch={handleSearch}
                      showSearchButton={!isSmallMobile}
                      compact={true}
                      sx={{
                        height: { xs: '44px', sm: '48px', md: '60px' },
                        minWidth: { xs: '120px', sm: '200px' },
                      }}
                    />
                  </Box>
                </Grow>
              </Box>
            </Fade>
          )}
        </Box>

        {/* Right Section - Icons & Profile */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexShrink: 0,
            ml: { xs: 0.5, sm: 1, md: 'auto' },
            gap: { xs: 0.25, sm: 0.5, md: 1 },
          }}
        >
          {/* Bookmark Icon - Hidden on smallest screens */}
          {!isSmallMobile && user.role === 'client' && (
            <IconButton
              onClick={() => navigate('/saved-therapists')}
              size={isSmallMobile ? 'small' : 'medium'}
              sx={{
                color: 'grey.600',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: 'primary.main',
                  transform: 'scale(1.1)',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
                p: { xs: 0.5, sm: 1 },
              }}
            >
              <Bookmark fontSize={isSmallMobile ? 'small' : 'medium'} />
            </IconButton>
          )}

          {/* Chat Icon - Hidden on smallest screens */}
          {!isSmallMobile && user.role === 'client' && (
            <IconButton
              onClick={() => navigate('/messages?patientId=' + user?.id)}
              size={isSmallMobile ? 'small' : 'medium'}
              sx={{
                color: 'grey.600',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: 'primary.main',
                  transform: 'scale(1.1)',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
                p: { xs: 0.5, sm: 1 },
              }}
            >
              <Chat fontSize={isSmallMobile ? 'small' : 'medium'} />
            </IconButton>
          )}

          {/* Notifications - shown for client and admin */}
          {(user.role === 'client' || user.role === UserRole.ADMIN) && (
            <IconButton
              onClick={handleNotificationMenuOpen}
              aria-label={`${unreadNotifications} unread notifications`}
              size={isSmallMobile ? 'small' : 'medium'}
              sx={{
                color: 'grey.600',
                transition: 'all 0.3s ease',
                '&:hover': {
                  color: 'primary.main',
                  transform: 'scale(1.1)',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                },
                p: { xs: 0.5, sm: 1 },
              }}
            >
              {user.role === UserRole.ADMIN ? (
                <Badge
                  badgeContent={unopenedCount}
                  color="primary"
                >
                  <NotificationsIcon fontSize={isSmallMobile ? 'small' : 'medium'} />
                </Badge>
              ) : (
                <Badge
                  badgeContent={notification.filter(n => !n.read).length}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#3bae78',
                      color: 'white',
                      fontSize: { xs: '0.6rem', sm: '0.7rem' },
                      height: { xs: 16, sm: 18 },
                      minWidth: { xs: 16, sm: 18 },
                    },
                  }}
                >
                  <NotificationsIcon fontSize={isSmallMobile ? 'small' : 'medium'} />
                </Badge>
              )}
            </IconButton>
          )}

          {/* Profile */}
          <span
            style={{
              display: 'flex',
              gap: '0px',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <IconButton
              onClick={handleProfileMenuOpen}
              aria-label="account of current user"
              size={isSmallMobile ? 'small' : 'medium'}
              sx={{
                ml: { xs: 0, sm: 0.5 },
                p: { xs: 0.25, sm: 0.5 },
              }}
            >
              {profilePhotoUrl ? (
                <Avatar
                  src={profilePhotoUrl}
                  alt={`${user?.firstName} ${user?.lastName}`}
                  sx={{
                    width: { xs: 28, sm: 32, md: 36 },
                    height: { xs: 28, sm: 32, md: 36 },
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: { xs: 28, sm: 32, md: 36 },
                    height: { xs: 28, sm: 32, md: 36 },
                    fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  }}
                >
                  {user?.firstName?.[0]}
                  {user?.lastName?.[0]}
                </Avatar>
              )}
            </IconButton>
            <div>
              {relationship && relationship.trim() !== '' && (
                <Chip
                  label={relationship}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontSize: { xs: '14px', sm: '0.7rem' },
                    height: { xs: 14, sm: 20 },
                    mt: '-8px',
                    ml: '4px',
                  }}
                />
              )}
            </div>
          </span>
        </Box>

        {/* Profile Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
          PaperProps={{
            sx: {
              minWidth: 240,
              mt: 4.6,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            },
          }}
        >
          {/* User Info Section - For Admin and Certificate Manager */}
          {(role === UserRole.ADMIN ||
            role === UserRole.CERTIFICATE_MANAGER) && (
              <>
                <Box sx={{ px: 2, py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={
                        user?.profilePhotoUrl ||
                        user?.avatar ||
                        undefined
                      }
                      alt={`${user?.firstName} ${user?.lastName}`}
                      sx={{ width: 36, height: 36 }}
                    >
                      {user?.firstName?.[0]}
                      {user?.lastName?.[0]}
                    </Avatar>
                    <Box
                      sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.5 }}
                        noWrap
                      >
                        {user?.email}
                      </Typography>
                      <Chip
                        label={
                          role === UserRole.ADMIN
                            ? 'Administrator'
                            : 'Certificate Manager'
                        }
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
              </>
            )}

          {isPrimaryClient && (
            <>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={
                      user?.profilePhotoUrl ||
                      user?.avatar ||
                      undefined
                    }
                    alt={`${user?.firstName} ${user?.lastName}`}
                    sx={{ width: 36, height: 36 }}
                  >
                    {user?.firstName?.[0]}
                    {user?.lastName?.[0]}
                  </Avatar>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    <Typography
                      variant="caption"
                      color="text.primary"
                      sx={{ display: 'block', mb: 0.5, fontWeight: 700, fontSize: '1rem' }}
                      noWrap
                    >
                      {user?.firstName} {user?.lastName}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.5 }}
                      noWrap
                    >
                      {user?.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
            </>
          )}



          {/* Account Settings - Only for Client and Therapist */}
          {(role === UserRole.CLIENT || role === UserRole.THERAPIST) && (
            <MenuItem onClick={(e) => handleProfile(e)} sx={{ py: 1.5 }}>
              <Settings sx={{ mr: 1.5, fontSize: '1.2rem' }} />
              <Typography variant="body2">Account settings</Typography>
            </MenuItem>
          )}

          {isPrimaryClient && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ px: 2, py: 0.5 }}>
                <Typography
                  variant="overline"
                  sx={{ color: 'text.secondary', letterSpacing: 0.5 }}
                >
                  Family Accounts
                </Typography>
              </Box>
              {familyLoading ? (
                <MenuItem disabled sx={{ py: 1 }}>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                    }}
                  >
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading family members...
                    </Typography>
                  </Box>
                </MenuItem>
              ) : familyError ? (
                <MenuItem disabled sx={{ py: 1 }}>
                  <Typography variant="body2" color="error">
                    {familyError}
                  </Typography>
                </MenuItem>
              ) : familyMembers.length === 0 ? (
                <MenuItem disabled sx={{ py: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    No family members added yet.
                  </Typography>
                </MenuItem>
              ) : (
                familyMembers.map(member => (
                  <MenuItem
                    key={member._id}
                    onClick={() => handleSwitchToFamilyMember(member)}
                    disabled={switchingMemberId === member._id}
                    sx={{ py: 1 }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          noWrap
                        >
                          {member.firstName} {member.lastName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          Switch to {member.firstName}
                        </Typography>
                      </Box>
                      {switchingMemberId === member._id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Chip
                          label="Switch"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))
              )}
            </>
          )}
          <MenuItem onClick={handleLogout} sx={{ py: 1 }}>
            <Logout sx={{ mr: 1.5, fontSize: '1.2rem' }} />
            <Typography variant="body2">Logout</Typography>
          </MenuItem>
        </Menu>

        {/* Notifications Menu */}
        <Menu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              width: 500,
              maxHeight: 600,
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'grey.50',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: 'grey.800',
              }}
            >
              Notifications
            </Typography>
          </Box>

          {/* Notifications List */}
          <Box
            sx={{
              overflowY: 'auto',
              maxHeight: 450,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(0,0,0,0.3)',
                },
              },
            }}
          >
            {notificationLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Loading notifications...
                </Typography>
              </Box>
            ) : notification?.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No notifications
                </Typography>
              </Box>
            ) : (
              <>
                {(showAllNotifications
                  ? notification
                  : notification.slice(0, NOTIFICATIONS_TO_SHOW)
                ).map((notificationItem, index) => (
                  <MenuItem
                    key={notificationItem._id}
                    onClick={() => {
                      handleNotificationClick(notificationItem)
                    }}
                    sx={{
                      cursor: 'pointer',
                      py: 0,
                      px: 0,
                      position: 'relative',
                      '&:hover': {
                        backgroundColor: notificationItem.read
                          ? 'action.hover'
                          : 'rgba(59, 174, 120, 0.08)',
                      },
                      borderBottom:
                        index <
                          (showAllNotifications
                            ? notification.length - 1
                            : Math.min(
                              notification.length,
                              NOTIFICATIONS_TO_SHOW
                            ) - 1)
                          ? '1px solid'
                          : 'none',
                      borderColor: 'grey.200',
                      // Left border indicator for unread notifications
                      borderLeft: notificationItem.read
                        ? 'none'
                        : '4px solid #3bae78',
                      bgcolor: notificationItem.read
                        ? 'transparent'
                        : 'rgba(59, 174, 120, 0.05)',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        p: 2,
                        position: 'relative',
                      }}
                    >
                      {/* Unread indicator dot */}
                      {!notificationItem.read && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: '#3bae78',
                          }}
                        />
                      )}
                      {/* Notification Header with Title and Timestamp */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: notificationItem.read ? 500 : 700,
                            color: notificationItem.read
                              ? 'grey.600'
                              : '#3bae78',
                            flex: 1,
                            pr: 1,
                          }}
                        >
                          {notificationItem.notificationTitle}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.75rem',
                            color: notificationItem.read
                              ? 'grey.500'
                              : 'grey.600',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                            fontWeight: notificationItem.read ? 400 : 500,
                          }}
                        >
                          {formatNotificationDate(notificationItem.createdAt)}
                        </Typography>
                      </Box>

                      {/* Notification Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '0.8125rem',
                          color: notificationItem.read
                            ? 'grey.500'
                            : 'grey.800',
                          lineHeight: 1.5,
                          mt: 0.5,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontWeight: notificationItem.read ? 400 : 500,
                        }}
                      >
                        {notificationItem?.notificationDescription}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </>
            )}
          </Box>

          {/* Show More/Less Button */}
          {notification?.length > NOTIFICATIONS_TO_SHOW && (
            <>
              <Divider />
              <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
                <Button
                  onClick={() => setShowAllNotifications(!showAllNotifications)}
                  endIcon={
                    showAllNotifications ? <ExpandLess /> : <ExpandMore />
                  }
                  sx={{
                    textTransform: 'none',
                    color: 'primary.main',
                    fontSize: '0.875rem',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  {showAllNotifications
                    ? 'Show Less'
                    : `Show More (${notification.length - NOTIFICATIONS_TO_SHOW} more)`}
                </Button>
              </Box>
            </>
          )}
        </Menu>
      </Toolbar>
    </AppBar>
  )
}

export default Navbar
